import React, { useEffect, useState } from "react";
import Modal from "../../../../components/Modal";
import TimePickerField from "./TimePickerField";
import {
  STAFF_SCHEDULE_MODAL_BTN,
  TIME_FIELD_LABEL,
  TIME_NOTES_TEXTAREA,
} from "./staffScheduleModalTheme";
import { PATIENT_THEME } from "../../patientTheme";
import { FORM_FIELD } from "../../../../ui/formTheme";
import {
  buildTime24,
  parseTime24,
  validateShiftTimes,
  findRoleOverlapError,
} from "./staffScheduleTime";
import { STAFF_SCHEDULE_DAYS } from "./staffScheduleConstants";

const defaultShift = () => ({
  start: parseTime24("08:00"),
  end: parseTime24("16:00"),
  breakStart: parseTime24("12:00"),
  breakEnd: parseTime24("13:00"),
});

const StaffScheduleTimeModal = ({
  isOpen,
  onClose,
  onSave,
  saving = false,
  staffRow,
  dayOfWeek,
  existingSlot,
  allRows,
}) => {
  const [shift, setShift] = useState(defaultShift());
  const [hasBreak, setHasBreak] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const dayLabel =
    STAFF_SCHEDULE_DAYS.find((d) => d.value === dayOfWeek)?.label || "Day";

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    if (existingSlot) {
      setShift({
        start: parseTime24(existingSlot.startTime),
        end: parseTime24(existingSlot.endTime),
        breakStart: parseTime24(existingSlot.breakStartTime || "12:00"),
        breakEnd: parseTime24(existingSlot.breakEndTime || "13:00"),
      });
      setHasBreak(
        Boolean(existingSlot.breakStartTime && existingSlot.breakEndTime),
      );
      setNotes(existingSlot.notes || "");
    } else {
      setShift(defaultShift());
      setHasBreak(false);
      setNotes("");
    }
  }, [isOpen, existingSlot, dayOfWeek]);

  const staffName = staffRow
    ? `${staffRow.firstName || ""} ${staffRow.lastName || ""}`.trim()
    : "";

  const handleSave = async () => {
    const startTime = buildTime24(
      shift.start.hour12,
      shift.start.minute,
      shift.start.period,
    );
    const endTime = buildTime24(
      shift.end.hour12,
      shift.end.minute,
      shift.end.period,
    );
    const breakStartTime = hasBreak
      ? buildTime24(
          shift.breakStart.hour12,
          shift.breakStart.minute,
          shift.breakStart.period,
        )
      : null;
    const breakEndTime = hasBreak
      ? buildTime24(
          shift.breakEnd.hour12,
          shift.breakEnd.minute,
          shift.breakEnd.period,
        )
      : null;

    const timeErr = validateShiftTimes({
      startTime,
      endTime,
      breakStartTime,
      breakEndTime,
      hasBreak,
    });
    if (timeErr) {
      setError(timeErr);
      return;
    }

    const overlapErr = findRoleOverlapError({
      candidate: {
        role: staffRow.role,
        employeeId: staffRow.employeeId,
        dayOfWeek,
        startTime,
        endTime,
      },
      rows: allRows,
      excludeSlotId: existingSlot?.id,
    });
    if (overlapErr) {
      setError(overlapErr);
      return;
    }

    setError("");
    await onSave({
      startTime,
      endTime,
      breakStartTime,
      breakEndTime,
      notes: notes.trim() || null,
    });
  };

  const title = existingSlot ? "Edit Time" : "Add Time";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        <div
          className={`rounded-lg border px-3 py-2.5 text-sm ${PATIENT_THEME.accentSoft}`}
        >
          {existingSlot ? "Update" : "Add"} time for{" "}
          <span className="font-semibold text-indigo-950">{staffName}</span> on{" "}
          <span className="font-semibold text-indigo-700">{dayLabel}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TimePickerField
            label="Start Time"
            {...shift.start}
            onChange={(next) => setShift((s) => ({ ...s, start: next }))}
          />
          <TimePickerField
            label="End Time"
            {...shift.end}
            onChange={(next) => setShift((s) => ({ ...s, end: next }))}
          />
        </div>

        <label className="flex items-center gap-2.5 py-1 text-sm font-medium text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hasBreak}
            onChange={(e) => setHasBreak(e.target.checked)}
            className={FORM_FIELD.checkbox}
          />
          Add Break (Optional)
        </label>

        {hasBreak ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-3 ml-0.5 border-l-[3px] border-indigo-200">
            <TimePickerField
              label="Break Start Time"
              {...shift.breakStart}
              onChange={(next) =>
                setShift((s) => ({ ...s, breakStart: next }))
              }
            />
            <TimePickerField
              label="Break End Time"
              {...shift.breakEnd}
              onChange={(next) => setShift((s) => ({ ...s, breakEnd: next }))}
            />
          </div>
        ) : null}

        <div className="rounded-lg border border-indigo-200 bg-white p-3 shadow-sm">
          <label className={TIME_FIELD_LABEL}>Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 200))}
            rows={3}
            placeholder="Enter notes here..."
            className={TIME_NOTES_TEXTAREA}
          />
          <p className="text-[11px] text-slate-400 mt-1.5 text-right tabular-nums">
            {notes.length}/200
          </p>
        </div>

        {error ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-3 mt-1 border-t border-indigo-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={STAFF_SCHEDULE_MODAL_BTN.cancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={STAFF_SCHEDULE_MODAL_BTN.primary}
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              "Save Time"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StaffScheduleTimeModal;
